// Message handler callback interface.
// Each handler receives this object instead of closing over MarketContext state directly.
// This makes handlers pure functions that are independently testable.

import type React from 'react';
import type {
  OrderBookEntry, Trade, Position, Stats, SymbolStats, BookState, BookAnalytics, Order,
  BookChangePacket, QuoteSuggestion, InventoryState, SettlementReport,
  PerformanceSnapshot, RiskEvent, ActivityEvent, RegimeState,
  SprintState, DailySummary, MonthlySummary, EngineConfig,
  EnhancedAnalyticsData,
} from '../../types';

type AnalyticsPt = { t: number; depthImbalance: number; ofi: number };

export type ModelHealth = {
  fill_prob_accuracy?: number;
  fill_prob_mae?: number;
  wait_time_mae?: number;
  calibration_error?: number;
  predictions_count?: number;
  last_updated?: string;
  model_type?: string;
  drift_detected?: boolean;
} | null;

export type DarHealthState = {
  fixLoggedOn: boolean;
  fixSessionStatus: 'CONNECTED' | 'DISCONNECTED' | 'RECONNECTING' | 'UNKNOWN';
  lastHeartbeatAt: string | null;
  pendingOrders: number;
  instrumentsLoaded: number;
  sessionId: string | null;
};

export type PortfolioState = {
  totalUnrealizedPnL: number;
  totalRealizedPnL: number;
  totalPnL: number;
  totalExposure: number;
};

export type RiskState = {
  grossNotional: number;
  longExposure: number;
  shortExposure: number;
  netExposure: number;
  capitalUtilization: number;
  priceSensitivity1pct: number;
  priceSensitivity5pct: number;
  realizedVolRisk: number;
  inventoryByTicker: Record<string, {
    quantity: number;
    settled_quantity: number;
    utilization: number;
    exit_ratio: number;
    price_sensitivity_1pct: number;
    price_sensitivity_5pct: number;
    capital_allocated: number;
    capital_used_pct: number;
    vol_risk_1sigma: number;
  }>;
};

export interface MessageCallbacks {
  // Context
  selectedSymbol: string;

  // Market data setters
  setOrderBook: React.Dispatch<React.SetStateAction<{ bids: OrderBookEntry[]; asks: OrderBookEntry[] }>>;
  setLastUpdateTime: React.Dispatch<React.SetStateAction<string>>;
  setCurrentPrice: React.Dispatch<React.SetStateAction<number>>;
  setStats: React.Dispatch<React.SetStateAction<Stats>>;
  setPriceMap: React.Dispatch<React.SetStateAction<Record<string, number>>>;

  // Analytics
  setAnalytics: React.Dispatch<React.SetStateAction<EnhancedAnalyticsData | null>>;
  setAnalyticsImbalanceBySymbol: React.Dispatch<React.SetStateAction<Map<string, AnalyticsPt[]>>>;

  // Trades
  setRecentTrades: React.Dispatch<React.SetStateAction<Trade[]>>;

  // Orders
  setOrders: React.Dispatch<React.SetStateAction<Order[]>>;

  // Positions
  setPositions: React.Dispatch<React.SetStateAction<Position[]>>;
  setCurrentPosition: React.Dispatch<React.SetStateAction<number>>;
  setPortfolio: React.Dispatch<React.SetStateAction<PortfolioState>>;
  setRisk: React.Dispatch<React.SetStateAction<RiskState>>;

  // Engine state
  setQuoteSuggestions: React.Dispatch<React.SetStateAction<QuoteSuggestion[]>>;
  setInventoryBySymbol: React.Dispatch<React.SetStateAction<Map<string, InventoryState>>>;
  /** Optional so the 5 existing tests that build a MessageCallbacks mock keep compiling. */
  setBookAnalyticsBySecId?: React.Dispatch<React.SetStateAction<Map<string, BookAnalytics>>>;
  setStatsBySecId?: React.Dispatch<React.SetStateAction<Map<string, SymbolStats>>>;
  setSettlementReport: React.Dispatch<React.SetStateAction<SettlementReport | null>>;
  setPerformanceSnapshot: React.Dispatch<React.SetStateAction<PerformanceSnapshot | null>>;
  setRiskEvents: React.Dispatch<React.SetStateAction<RiskEvent[]>>;
  setModelHealth: React.Dispatch<React.SetStateAction<ModelHealth>>;
  setRegimeBySymbol: React.Dispatch<React.SetStateAction<Map<string, RegimeState>>>;
  setSprintState: React.Dispatch<React.SetStateAction<SprintState | null>>;
  setDailySummary: React.Dispatch<React.SetStateAction<DailySummary | null>>;
  setMonthlySummary: React.Dispatch<React.SetStateAction<MonthlySummary | null>>;
  pushActivity: (ev: ActivityEvent) => void;

  // Control plane
  addAlert?: (alert: import('../../types/control').Alert) => void;
  onCommandAck?: (ack: import('../../types/control').CommandAckMessage) => void;

  // System
  setDarHealth: React.Dispatch<React.SetStateAction<DarHealthState>>;
  setConfigAckStatus: React.Dispatch<React.SetStateAction<'idle' | 'pending' | 'ok' | 'error'>>;
  setConfigAckTime: React.Dispatch<React.SetStateAction<string | null>>;
  setConfigAckErrors: React.Dispatch<React.SetStateAction<Record<string, string> | null>>;
  setEngineConfig: React.Dispatch<React.SetStateAction<EngineConfig | null>>;

  // Chart data — dispatched to Redux store (not React state)
  chartDispatch: (action: { type: string; payload: unknown }) => void;

  // Book changes
  setBookChanges: React.Dispatch<React.SetStateAction<BookChangePacket[]>>;

  // Refs (mutable, passed by reference)
  booksBySecIdRef: React.MutableRefObject<Map<string, BookState>>;
  statsBySecIdRef: React.MutableRefObject<Map<string, Stats>>;
  lastUpdateBySecIdRef: React.MutableRefObject<Map<string, string>>;
  tradesBySecIdRef: React.MutableRefObject<Map<string, Trade[]>>;
  lastFillBySecIdRef: React.MutableRefObject<Map<string, { side: 'buy' | 'sell'; price: number; size: number; time?: string }>>;
  lastMlFillUpdateMsBySecIdRef: React.MutableRefObject<Map<string, number>>;
  lastOfiActivityBySecIdRef: React.MutableRefObject<Map<string, { value: number; ts: number }>>;
  analyticsHistoryRef: React.MutableRefObject<Map<string, AnalyticsPt[]>>;
  lastAnalyticsBySecIdRef: React.MutableRefObject<Map<string, EnhancedAnalyticsData>>;

  // Utilities
  labelForSecId: (s: string) => string;
  getMinimalAnalytics: () => EnhancedAnalyticsData;
  nextActivitySeq: () => number;
}

/** Normalize a raw timestamp (Unix seconds, Unix ms, or ISO string) to ISO string. */
export function normalizeTimestamp(raw: string | number | undefined): string {
  if (!raw) return new Date().toISOString();
  if (typeof raw === 'string') return raw;
  return raw < 1e12
    ? new Date(raw * 1000).toISOString()
    : new Date(raw).toISOString();
}
